import { useEffect, useState } from "react";

import { Button, Grid, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CheckBoxOutlined, CreateOutlined } from "@mui/icons-material";

import nameof from "ts-nameof.macro";

import { AdminRoutesConfig } from "../../../../RoutesConfig";
import LinearStepper from "../../../../../common/components/LinearStepper";
import Success from "../../../../shared/SuccessModal";
import CheckboxExtended from "../../../../../common/components/mui-extended/CheckboxExtended";
import DatePickerExtended from "../../../../../common/components/mui-extended/DatePickerExtended";
import SectionExtended from "../../../../../common/components/mui-extended/SectionExtended";
import TextFieldExtended from "../../../../../common/components/mui-extended/TextFieldExtended";
import { ErrorMessages, Rules } from "../../../../../common/validationRules";
import { useAxiosLoader } from "../../../../../common/hooks/useAxiosLoader";
import { Mode, Step, useInputForm } from "../../../../../common/hooks/useInputForm";
import { ExternalUserInputDto } from "../../../../../interfaces/admin/ExternalUser";
import externalUserService from "../../../../../services/externalUserService";
import userService from "../../../../../services/userService";
import CheckboxSelectExtended, {
  EnumNomenclature,
} from "../../../../../common/components/mui-extended/CheckboxSelectExtended";

export default function Input() {
  const EXTERNAL_USER_EXPIRY_DAYS = 15;
  const loading = useAxiosLoader(false);

  const defaultModel: ExternalUserInputDto = {
    isActive: true,
    validFrom: new Date(),
    validTo: new Date(new Date().getTime() + EXTERNAL_USER_EXPIRY_DAYS * 24 * 60 * 60 * 1000),
    permissionsForRegister: [], //initSearchPermissionsForRegister(),
  };

  const [permissionOptions, setPermissionOptions] = useState<Array<EnumNomenclature>>([]);

  const form = useInputForm<ExternalUserInputDto>({
    defaultModel: defaultModel,
    addAsync: externalUserService.addExternalUserAsync,
    updateAsync: externalUserService.updateExternalUserAsync,
    validate: async (model: ExternalUserInputDto) => {
      if (model.username) {
        await externalUserService.getExternalUserIdByUsernameAsync(model.username).then(({ data: externalUserId }) => {
          if (externalUserId && externalUserId !== form.id) {
            form.setError(nameof(defaultModel.username), ErrorMessages.ExternalUserUsernameExists);
          }
        });
      }

      if (model.thumbprint) {
        await userService.getCertificateIdByThumbprintAsync(model.thumbprint).then(({ data: certificateId }) => {
          if (certificateId && certificateId !== model.certificateId) {
            form.setError(nameof(defaultModel.thumbprint), ErrorMessages.ThumbprintExists);
          }
        });
      }

      if (model.validFrom && model.validTo) {
        if (model.validFrom > model.validTo) {
          form.setError(nameof(defaultModel.validFrom), '"Активен от дата" трябва да е по-малка от "Активен до дата".');
        }

        const nextYearDate = new Date(model.validFrom);
        nextYearDate.setFullYear(nextYearDate.getFullYear() + 1);

        if (model.validTo > nextYearDate)
          form.setError(nameof(defaultModel.validTo), "Максимален допустим период е една година.");
      }
    },
  });

  const loadPermissionOptions = () => {
    (async () => {
      await externalUserService
        .getPermissionRegistersAsync()
        .then(({ data: options }) => setPermissionOptions(options));
    })();
  };

  useEffect(() => {
    loadPermissionOptions();

    (async () => {
      if (form.mode === Mode.EDIT) {
        await externalUserService.getExternalUserAsync(form.id).then(({ data: externalUser }) => {
          const model: ExternalUserInputDto = {
            ...externalUser,
            externalUserId: form.id,
          };

          form.recreateForm(model);
        });
      }
    })();
  }, []);

  const renderStepper = () => <LinearStepper step={form.step} mode={form.mode} />;

  const renderSuccess = () => (
    <Success routePath={AdminRoutesConfig.ExternalUser}>
      {`на външен потребител ${form.getValues(nameof(defaultModel.name))}`}
    </Success>
  );

  const renderForm = () => (
    <form onSubmit={form.onSubmit}>
      <Stack spacing={3}>
        <SectionExtended title="Лични данни">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextFieldExtended
                label="Име"
                name={nameof(defaultModel.name)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={Rules.Required}
              />
            </Grid>
          </Grid>
        </SectionExtended>
        <SectionExtended title="Потребителски данни">
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextFieldExtended
                label="Потребителско име"
                name={nameof(defaultModel.username)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={{
                  ...Rules.Required,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextFieldExtended
                label="Парола"
                name={nameof(defaultModel.password)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={{ ...Rules.Required, ...Rules.Password(6, 12) }}
              />
            </Grid>
            <Grid item xs={6} />
            <Grid item xs={12} md={6}>
              <TextFieldExtended
                label="SHA-1 на публичния ключ (на електронния подпис)"
                name={nameof(defaultModel.thumbprint)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={Rules.Thumbprint}
              />
            </Grid>
            <Grid item xs={12} md={3} display="flex" alignItems="center">
              <CheckboxExtended
                label="Активен"
                name={nameof(defaultModel.isActive)}
                form={form}
                disabled={form.step === Step.PREVIEW}
              />
            </Grid>
          </Grid>
        </SectionExtended>
        <SectionExtended title="Права">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <DatePickerExtended
                label="Активен от дата"
                name={nameof(defaultModel.validFrom)}
                form={form}
                maxDate={form.watch(nameof(defaultModel.validTo))}
                disabled={form.step === Step.PREVIEW}
                rules={Rules.Required}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePickerExtended
                label="Активен до дата"
                name={nameof(defaultModel.validTo)}
                minDate={form.watch(nameof(defaultModel.validFrom))}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={Rules.Required}
              />
            </Grid>
            <Grid item xs={4} />
            <Grid item xs={12}>
              <CheckboxSelectExtended
                name={nameof(defaultModel.permissionsForRegister)}
                form={form}
                label="Права"
                options={permissionOptions}
                disabled={form.step === Step.PREVIEW}
                rules={Rules.Required}
              />
            </Grid>
          </Grid>
        </SectionExtended>
        <Stack direction="row-reverse" justifyContent="space-between">
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingPosition="start"
            size="large"
            startIcon={<CheckBoxOutlined />}
            onClick={() => form.scrollToFirstError()}
            type="submit"
          >
            {form.step === Step.INPUT ? "Преглед" : "Впиши"}
          </LoadingButton>

          {form.step === Step.PREVIEW && (
            <Button size="large" startIcon={<CreateOutlined />} onClick={() => form.onEdit()}>
              Редактирай
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Външни потребители</Typography>
      {renderStepper()}
      {form.step === Step.SUCCESS ? renderSuccess() : renderForm()}
    </Stack>
  );
}
